import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Form, Input, Result, Space, Switch, Tabs, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  useClientsGetClient,
  useClientsUpdateClient,
} from '@/api/generated/endpoints/clients/clients';
import type { ClientDetail, ClientUpdate } from '@/api/generated/model';
import { ApiProblem } from '@/api/http';
import { useHasRole } from '@/auth/useHasRole';
import { ArticlesTab } from '@/pages/catalog/ArticlesTab';
import { CLIENT_FIELD_BY_CODE, innRules, kppRules } from '@/pages/catalog/clientForm';
import { DeliveryPointsTab } from '@/pages/catalog/DeliveryPointsTab';
import { problemMessage } from '@/i18n/problem';
import { applyProblem } from '@/lib/problemForm';
import { blankToNull } from '@/lib/text';
import { invalidatePath } from '@/lib/queryCache';

export function ClientPage() {
  const { t } = useTranslation();
  const clientId = Number(useParams().clientId);
  const navigate = useNavigate();
  const detail = useClientsGetClient(clientId, { query: { enabled: Number.isInteger(clientId) } });
  const back = (
    <Button
      type="text"
      icon={<ArrowLeftOutlined />}
      onClick={() => {
        void navigate('..');
      }}
    />
  );
  if (detail.error instanceof ApiProblem && detail.error.status === 404) {
    return <Result status="404" title={t('clients.notFound')} extra={back} />;
  }
  const c = detail.data;
  return (
    <Card
      loading={detail.isPending}
      title={
        <Space>
          {back}
          {c?.name}
          {c?.brand && <Tag color="blue">{c.brand}</Tag>}
          {c && !c.is_active && <Tag>{t('common.archived')}</Tag>}
        </Space>
      }
    >
      {c && (
        <Tabs
          items={[
            {
              key: 'points',
              label: t('clients.tabPoints'),
              children: <DeliveryPointsTab client={c} />,
            },
            {
              key: 'articles',
              label: t('clients.tabArticles'),
              children: <ArticlesTab clientId={c.id} />,
            },
            {
              key: 'details',
              label: t('clients.tabDetails'),
              children: <ClientDetailsForm client={c} />,
            },
          ]}
        />
      )}
    </Card>
  );
}

function ClientDetailsForm({ client }: { client: ClientDetail }) {
  const { t } = useTranslation();
  const canEdit = useHasRole('manager');
  const [form] = Form.useForm<ClientUpdate>();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  useEffect(() => {
    form.setFieldsValue({
      name: client.name,
      brand: client.brand,
      inn: client.inn,
      kpp: client.kpp,
      is_active: client.is_active,
    });
  }, [form, client]);
  const update = useClientsUpdateClient({
    mutation: {
      onSuccess: async () => {
        await invalidatePath(queryClient, '/api/v1/clients');
        void message.success(t('common.saved'));
      },
      onError: (e) => {
        if (!applyProblem(form, e, CLIENT_FIELD_BY_CODE)) void message.error(problemMessage(e));
      },
    },
  });

  return (
    <Form<ClientUpdate>
      form={form}
      layout="vertical"
      disabled={!canEdit}
      style={{ maxWidth: 560 }}
      onFinish={(v) => {
        update.mutate({
          clientId: client.id,
          data: { ...v, kpp: blankToNull(v.kpp), brand: blankToNull(v.brand) },
        });
      }}
    >
      <Form.Item
        name="name"
        label={t('clients.name')}
        rules={[{ required: true, whitespace: true, message: t('clients.nameRequired') }]}
      >
        <Input maxLength={300} />
      </Form.Item>
      <Form.Item name="brand" label={t('clients.brand')}>
        <Input maxLength={100} />
      </Form.Item>
      <Space>
        <Form.Item
          name="inn"
          label={t('clients.inn')}
          rules={innRules(t)}
          extra={t('clients.innLocked')}
        >
          <Input maxLength={12} inputMode="numeric" />
        </Form.Item>
        <Form.Item name="kpp" label={t('clients.kppHead')} rules={kppRules(t)}>
          <Input maxLength={9} />
        </Form.Item>
      </Space>
      <Form.Item name="is_active" label={t('common.active')} valuePropName="checked">
        <Switch />
      </Form.Item>
      {canEdit && (
        <Button type="primary" htmlType="submit" loading={update.isPending}>
          {t('common.save')}
        </Button>
      )}
    </Form>
  );
}
